import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';
import { create } from 'zustand';
import { defaultWMS1101_R01DetailDto, defaultWMS1101_R01HeaderDto, WMS1101_R01DetailDto, WMS1101_R01HeaderDto } from './Dto_WMS1101_R01';
import { getFutureWeekdayExceptTodayAndTomorrow } from 'src/utils/date/dateUtils';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';


export interface WMS1101_R01Filter {
	dateRange: DateRange | undefined;
	customCd: string;
	customNm: string;
}

export interface Store_WMS1101_R01 {
	WMS1101_R01Filter: WMS1101_R01Filter;
	WMS1101_R01PastFilter: WMS1101_R01Filter;
	setWMS1101_R01Filter: (filter: Partial<Store_WMS1101_R01['WMS1101_R01Filter']>) => void;
	setWMS1101_R01PastFilter: (filter: Store_WMS1101_R01['WMS1101_R01PastFilter']) => void;

	WMS1101_R01List: WMS1101_R01HeaderDto[];
	setWMS1101_R01List: (dataList: WMS1101_R01HeaderDto[]) => void;

	selectedWMS1101_R01: WMS1101_R01HeaderDto | undefined;
	setSelectedWMS1101_R01: (data: WMS1101_R01HeaderDto | undefined) => void;
	initSelectedWMS1101_R01: () => void;

	WMS1101_R01DetailList: WMS1101_R01DetailDto[];
	setWMS1101_R01DetailList: (data: WMS1101_R01DetailDto[]) => void;

	selectedWMS1101_R01DetailList: string[];
	setSelectedWMS1101_R01DetailList: (idList: string[]) => void;
	initSelectedWMS1101_R01DetailList: () => void;

	originalWMS1101_R01DetailList: WMS1101_R01DetailDto[];
	setOriginalWMS1101_R01DetailList: (data: WMS1101_R01DetailDto[]) => void;

	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;
	detailColumnState: ColumnState[] | null;
	setDetailColumnState: (state: ColumnState[] | null) => void;

	allCount: number;
	setAllCount: (allCount: number) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	detailScrollTop: number | null;
	setDetailScrollTop: (top: number | null) => void;

	initData: () => void;
}

export const defaultWMS1101_R01Filter: WMS1101_R01Filter = {
	dateRange: {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: getFutureWeekdayExceptTodayAndTomorrow()
	},
	customCd: '',
	customNm: '',
};

const getInitialWMS1101_R01HeaderDto = (): WMS1101_R01HeaderDto => {
	const { wmsCdList, warehouseCdList } = useCommonCodeStore.getState();
	return {
		...defaultWMS1101_R01HeaderDto,
		logicCenter: wmsCdList?.[0]?.minorCd || '',
		warehouseCd: warehouseCdList?.[0]?.warehouseCd || '',
		warehouseNm: warehouseCdList?.[0]?.warehouseNm || '',
	};
};

export const useWMS1101_R01Store = create<Store_WMS1101_R01>((set) => ({
	WMS1101_R01Filter: defaultWMS1101_R01Filter,
	WMS1101_R01PastFilter: defaultWMS1101_R01Filter,
	setWMS1101_R01Filter: (update) => set((state) => ({
		WMS1101_R01Filter: {
			...state.WMS1101_R01Filter,
			...update
		}
	})),
	setWMS1101_R01PastFilter: (filter) => set((state) => ({
		...state,
		WMS1101_R01PastFilter: filter
	})),

	WMS1101_R01List: [],
	setWMS1101_R01List: (dataList) => set((state) => ({
		...state,
		WMS1101_R01List: dataList
	})),

	selectedWMS1101_R01: undefined,
	setSelectedWMS1101_R01: (data) => set((state) => ({
		selectedWMS1101_R01: data,
		selectedWMS1101_R01DetailList: [],
		detailScrollTop: null,
	})),
	initSelectedWMS1101_R01: () => {
		set({
			selectedWMS1101_R01: undefined,
			WMS1101_R01DetailList: []
		});
	},

	WMS1101_R01DetailList: [],
	setWMS1101_R01DetailList: (data) => set((state) => {
		let newList: WMS1101_R01DetailDto[] = [];
		let valueAndOrderIndex = 1;
		data.forEach((item) => {
			newList.push({
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueAndOrderIndex,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueAndOrderIndex,
				orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			WMS1101_R01DetailList: newList
		};
	}),

	selectedWMS1101_R01DetailList: [],
	setSelectedWMS1101_R01DetailList: (idList) => set((state) => ({
		...state,
		selectedWMS1101_R01DetailList: idList,
	})),
	initSelectedWMS1101_R01DetailList: () => set({ selectedWMS1101_R01DetailList: [] }),

	originalWMS1101_R01DetailList: [],
	setOriginalWMS1101_R01DetailList: (data) => set((state) => {
		let newList: WMS1101_R01DetailDto[] = [];
		let valueAndOrderIndex = 1;
		data.forEach((item) => {
			newList.push({
				...defaultWMS1101_R01DetailDto,
				...item,
				valueIndex: valueAndOrderIndex,
				orderIndex: valueAndOrderIndex,
				orderIndexSeq: valueAndOrderIndex
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			originalWMS1101_R01DetailList: newList
		};
	}),

	columnState: null,
	setColumnState: (state) => set({ columnState: state }),
	detailColumnState: null,
	setDetailColumnState: (state) => set({ detailColumnState: state }),

	allCount: 0,
	setAllCount: (allCount) => set({ allCount }),

	scrollTop: null,
	setScrollTop: (top) => set({ scrollTop: top }),
	detailScrollTop: null,
	setDetailScrollTop: (top) => set({ detailScrollTop: top }),

	initData: () => {
		set((state) => ({
			...state,
			WMS1101_R01Filter: defaultWMS1101_R01Filter,
			WMS1101_R01PastFilter: defaultWMS1101_R01Filter,
			WMS1101_R01List: [],
			selectedWMS1101_R01: undefined,
			WMS1101_R01DetailList: [],
			selectedWMS1101_R01DetailList: [],
			originalWMS1101_R01DetailList: [],
			editWMS1101_R01: getInitialWMS1101_R01HeaderDto(),
			allCount: 0,
			scrollTop: null,
			detailScrollTop: null,
		}));
	},
}));
